import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ProductConfig } from 'app/model/product-config';
import { BehaviorSubject, forkJoin } from 'rxjs';
import { PdfViewerComponent } from 'app/components/pdf-viewer/pdf-viewer.component';
import { IonContent, ModalController } from '@ionic/angular';
import { ProductListState } from 'app/interfaces/user.interface';
import { APP_CONSTANTS } from 'app/app.constants';

@Injectable({
  providedIn: 'root',
})
export class MenuNavigationService {
  public selectedProductIndex = -1;
  public selectedSectionIndex = -1;
  public selectedSubSectionIndex = -1;
  public selectedProductFromMegaMenu = -1;
  public clinicalProductIndex = 0;
  public clinicalSectionIndex = 3;
  public previousStudyLabel = '';
  public nextStudyLabel = '';
  public isClinicalStudies = false;
  public title: string;
  public subTitle: string;
  public productList = [];
  public pdfLink = '';
  public productsOverview= [];
  public productListObservable = new BehaviorSubject<ProductListState>({
    selectedProductIndex: 0,
    selectedSectionIndex: 0,
  });
  // To refresh references data
  public getReferencesClicked = new BehaviorSubject(false);
  isDataAvailable = this.getReferencesClicked.asObservable();

  public setReferencesClicked(value: boolean) {
    this.getReferencesClicked.next(value);
  }

  constructor(
    public router: Router,
    private readonly http: HttpClient,
    private readonly modalController: ModalController
  ) {


    this.initialize();
  }

  public initialize() {
    this.loadProductJson();
  }

  public updateProductList(state: ProductListState) {
    this.productListObservable.next(state);
  }

  public viewProductPI(link?) {
    if (link) {
      this.pdfLink = link;
    } else if (this.selectedProductFromMegaMenu >= 0 && this.selectedProductFromMegaMenu < this.productList.length) {
      this.pdfLink = this.productList[this.selectedProductFromMegaMenu].pdfLink;
    }else if (this.selectedProductIndex >= 0 && this.selectedProductIndex < this.productList.length) {
      this.pdfLink = this.productList[this.selectedProductIndex].pdfLink;
    }
    this.openPDF(this.pdfLink);
  }

  public async openPDF(fileLink) {
    const pdfModal = await this.modalController.create({
      component: PdfViewerComponent,
      cssClass: 'video-popup',
      componentProps: {
        state: fileLink,
      },
      backdropDismiss: true,
    });
    await pdfModal.present();
  }

  public navigateByUrl(pageName: string, params?) {
    let navParams;
    if (params) {
      navParams = { ...params, replaceUrl: true };
    } else {
      navParams = { replaceUrl: true };
    }
    this.router.navigateByUrl(pageName, navParams);
  }

  public navigate(pageName: string, param) {
    this.router.navigate([pageName], { queryParams: param });
  }

  public selectProductFromSitemap(productIndex: number, sectionIndex) {
    // Set the selected indices
    this.selectedProductIndex = productIndex;
    this.selectedSectionIndex = sectionIndex;
    this.selectedSubSectionIndex = 0;

    // Update the observable to notify components
    this.updateProductList({
      selectedProductIndex: productIndex,
      selectedSectionIndex: sectionIndex
    });

    // Update titles if product data is available
    if (this.productList && this.productList[productIndex]) {
      this.title = this.productList[productIndex].productName || `Product ${productIndex}`;
      if (this.productList[productIndex].sections && this.productList[productIndex].sections[sectionIndex]) {
        this.subTitle = this.productList[productIndex].sections[sectionIndex].title;
      }
    }

    this.router.navigate([APP_CONSTANTS.ROUTE_PATHS.PRODUCT_CONTENT], {
      queryParams: {
        selectedProductIndex: productIndex,
        selectedSectionIndex: sectionIndex,
        fromSitemap: true
      }
    });
  }

  public resetData() {
    this.title = '';
    this.subTitle = '';
    this.selectedProductIndex = -1;
    this.selectedSectionIndex = -1;
    this.selectedSubSectionIndex = -1;
    this.selectedProductFromMegaMenu = -1;
    this.isClinicalStudies = false;
    this.previousStudyLabel = '';
    this.nextStudyLabel = '';
    this.checkClinicalStudies();
  }

  public loadProductJson() {
    return new Promise((resolve) => {
      this.http.get('assets/data/en-US/product_config.json').subscribe((allProducts: ProductConfig[]) => {
        this.productsOverview = allProducts.filter(product => product.isActive);

        const productsToFetch = [];
        for(const product of this.productsOverview) {
          const data = this.http.get(`assets/data/en-US/products/${product.id}/info.json`);
          productsToFetch.push(data);
        }

        forkJoin(productsToFetch).subscribe(val => {  // [thrombinData, gelFlowNTData, gelFlowKitData, thrombiGelData]
          this.productList = val;
          resolve(this.productList);
        });
      });
    });
  }

  public scrollToElement(id: string) {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'instant' });
    }
  }

  public scrollToTop() {
    const element = document.getElementById('product-content') as unknown as IonContent;
    if (element) {
      element.scrollToTop();
    }
  }

  public checkClinicalStudies() {
    this.isClinicalStudies = this.selectedProductIndex === this.clinicalProductIndex && this.selectedSectionIndex === this.clinicalSectionIndex;
    if(this.isClinicalStudies) {
      if(this.selectedSubSectionIndex !== 0) {
        if (this.selectedSubSectionIndex > 0 && this.productList[this.selectedProductIndex]?.sections[this.selectedSectionIndex]?.sections) {
          this.previousStudyLabel = this.productList[this.selectedProductIndex].sections[this.selectedSectionIndex].sections[this.selectedSubSectionIndex - 1].title;
        }
      }
      if(this.selectedSubSectionIndex !== 4) {
        if (this.productList[this.selectedProductIndex]?.sections[this.selectedSectionIndex]?.sections) {
          this.nextStudyLabel = this.productList[this.selectedProductIndex].sections[this.selectedSectionIndex].sections[this.selectedSubSectionIndex + 1]?.title ?? '';
        }
      }
    } else {
      this.previousStudyLabel = '';
      this.nextStudyLabel = '';
    }
  }
}
